

























































































































































import { Vue, Component } from 'vue-property-decorator';
import { mapState } from 'vuex';
import ApiHelper from 'api-helper';
import lodash from 'lodash';
import moment from 'moment';

import { Order } from '@/models/order';
import { PaymentMethodIdDisplay } from '@common/enums/order';
import iconOrder from '@/assets/img/icon-order.svg';
import ContentLayout from '@components/layouts/Content.vue';
import OrderTrackingTable from '@components/order/OrderTrackingTable.vue';
import ModalCancel from '@components/order/ModalCancel.vue';
import ModalReopen from '@components/order/ModalReopen.vue';
import ModalHistoryPayment from '@components/order/ModalHistoryPayment.vue';
import ModalMakeDelivery from '@components/order/ModalMakeDelivery.vue';
import OrderBulkActionModal from '@components/order/OrderBulkActionModal.vue';
import { PLAN_PAYMENT_TYPE } from '@common/constants/plan';
import { Tracking } from '@/models/tracking';
import { DeliveryStateType } from '@common/enums/order';
import Toast from '@common/helpers/toast';

@Component({
  computed: {
    ...mapState('auth', ['currentUser']),
  },
  data() {
    return {
      iconHeader: iconOrder,
      lodash,
      moment,
      PaymentMethodIdDisplay,
      PLAN_PAYMENT_TYPE,
    };
  },
  components: {
    ContentLayout,
    OrderTrackingTable,
    ModalCancel,
    ModalReopen,
    ModalHistoryPayment,
    ModalMakeDelivery,
    OrderBulkActionModal,
  },
})
export default class OrderDetail extends Vue {
  private order: Order | object = {};
  private firstOrderTracking: Tracking | object = {};
  private isLoadingOrder: boolean = false;
  private selectedTrackingIds: number[] = [];
  private skippableIds: number[] = [];
  private unskippableIds: number[] = [];
  private DeliveryStateType = DeliveryStateType;

  get canManageSkip() {
    return (
      (this as any).currentUser &&
      ((this as any).currentUser.isSuperAdminRole ||
        (this as any).currentUser.isCSRole)
    );
  }

  get selectedTrackings() {
    return (lodash.get(this.order, 'tracking', []) as Tracking[]).filter(
      (t) => t.id && this.selectedTrackingIds.includes(t.id),
    );
  }

  get selectedSkipCount() {
    return this.selectedTrackings.filter(
      (t) =>
        !!t.delivery_date &&
        lodash.includes(
          [
            DeliveryStateType.ReadyForDelivery,
            DeliveryStateType.PreparingForDelivery,
            DeliveryStateType.NotPaid,
          ],
          t.state,
        ),
    ).length;
  }

  get selectedUnskipCount() {
    return this.selectedTrackings.filter(
      (t) =>
        t.state === DeliveryStateType.Skipped &&
        t.delivery_date &&
        moment(t.delivery_date).isAfter(moment(), 'day'),
    ).length;
  }

  get skippableDates() {
    return this.selectedTrackings
      .filter(
        (t) =>
          !!t.delivery_date &&
          lodash.includes(
            [
              DeliveryStateType.ReadyForDelivery,
              DeliveryStateType.PreparingForDelivery,
              DeliveryStateType.NotPaid,
            ],
            t.state,
          ),
      )
      .map((t) => moment(t.delivery_date as any).format('YYYY/MM/DD'));
  }

  get unskippableDates() {
    return this.selectedTrackings
      .filter(
        (t) =>
          t.state === DeliveryStateType.Skipped &&
          t.delivery_date &&
          moment(t.delivery_date).isAfter(moment(), 'day'),
      )
      .map((t) => moment(t.delivery_date as any).format('YYYY/MM/DD'));
  }

  mounted() {
    this.fetchOrder();
  }

  backToList() {
    const queryParams = lodash.pickBy(
      {
        ...this.$route.query,
        page: this.$route.query.page ? this.$route.query.page.toString() : '1',
      },
      lodash.identity,
    );

    this.$router.push({
      name: 'OrderList',
      query: queryParams,
    });
  }

  private bulkSkip() {
    this.skippableIds = this.selectedTrackings
      .filter(
        (t) =>
          !!t.delivery_date &&
          lodash.includes(
            [
              DeliveryStateType.ReadyForDelivery,
              DeliveryStateType.PreparingForDelivery,
              DeliveryStateType.NotPaid,
            ],
            t.state,
          ),
      )
      .map((t) => t.id as number);

    if (this.skippableIds.length === 0) {
      return;
    }

    this.$modal.show('modalBulkSkip');
  }

  private async onBulkSkipConfirm() {
    this.$modal.hide('modalBulkSkip');
    try {
      this.$store.commit('auth/updateLoading', true);
      await ApiHelper.getApi('OrderV2Api').bulkSkip({
        orders_tracking_ids: this.skippableIds,
      });
      Toast.success('スキップしました');
      this.selectedTrackingIds = [];
      this.fetchOrder();
    } catch (error) {
      const err = error as any;
      Toast.error(err.response?.data?.message || 'エラーが発生しました');
    } finally {
      this.$store.commit('auth/updateLoading', false);
    }
  }

  private bulkUnskip() {
    this.unskippableIds = this.selectedTrackings
      .filter(
        (t) =>
          t.state === DeliveryStateType.Skipped &&
          t.delivery_date &&
          moment(t.delivery_date).isAfter(moment(), 'day'),
      )
      .map((t) => t.id as number);

    if (this.unskippableIds.length === 0) {
      return;
    }

    this.$modal.show('modalBulkUnskip');
  }

  private async onBulkUnskipConfirm() {
    this.$modal.hide('modalBulkUnskip');
    try {
      this.$store.commit('auth/updateLoading', true);
      await ApiHelper.getApi('OrderV2Api').bulkUnskip({
        orders_tracking_ids: this.unskippableIds,
      });
      Toast.success('スキップを解除しました');
      this.selectedTrackingIds = [];
      this.fetchOrder();
    } catch (error) {
      const err = error as any;
      Toast.error(err.response?.data?.message || 'エラーが発生しました');
    } finally {
      this.$store.commit('auth/updateLoading', false);
    }
  }

  private fetchOrder() {
    this.isLoadingOrder = true;
    const opts = {
      with: 'plan,user,tracking,tracking.ordersTrackingHistoryRefunded',
    };
    const orderId = this.$route.params.id;

    ApiHelper.getApi('OrderApi')
      .getOrderDetail(orderId, opts)
      .then((res: any) => {
        this.order = new Order().deserialize(res);
        this.firstOrderTracking =
          lodash.find(res.tracking, (o: any) => o.delivery_sequence == 1) || {};
        this.isLoadingOrder = false;
      });
  }

  private redirectUserInfo() {
    const userId = lodash.get(this.order, 'user_id');

    this.$router.push(`/v2/patients/${userId}`);
  }

  private getUserName() {
    return (
      `${lodash.get(this.order, 'user.family_name', '')} ${lodash.get(
        this.order,
        'user.given_name',
        '',
      )}`.trim() || '-'
    );
  }
}
